import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';
import parse from 'html-react-parser';

import {
  PageContainer,
  PageHero,
  Section,
  EmptyState,
  Skeleton,
} from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

function slugify(s) {
  return String(s || '').toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
}

const I = {
  Trophy: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/><path d="M10 14.66V17c0 .55.47.98.97 1.21C12.15 18.75 13 19.85 13 21"/>
      <path d="M14 14.66V17c0 .55-.47.98-.97 1.21C11.85 18.75 11 19.85 11 21"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Calendar: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Camera: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
};

export const AwardsAndNews = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'awards' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({ seo_title: 'Awards & News | ESAY', seo_desc: 'Achievements and industry recognition for ESAY.', seo_keywords: 'esay awards, news' });
    });
    Axios.get('/new_apis/get-all-articles', { headers: { key } }).then((res) => {
      setData(res.data.articles || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <PageContainer maxWidth="lg">
        <PageHero
          compact
          kicker="Honors & Highlights"
          title="Awards & News"
          subtitle="Achievements, milestones, and industry recognition for ESAY and Arawinz."
        />

        <Section>
          {loading ? (
            <div className="grid grid-cols-1 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rect" height={220} />
              ))}
            </div>
          ) : data.length === 0 ? (
            <EmptyState
              icon={<I.Trophy className="h-7 w-7" />}
              title="No articles yet"
              subtitle="Check back soon for awards and news updates."
            />
          ) : (
            <div className="grid grid-cols-1 gap-5">
              {data.map((i) => {
                const slug = slugify(i.article_title);
                const images = (i.article_images || '').split(',').filter(Boolean);
                const hero = images[0];
                const restThumbs = images.slice(1, 4);
                const extra = images.length - 4;
                const totalImages = images.length;

                return (
                  <RouterLink
                    key={i.article_id}
                    to={`/article-details/${slug}/${i.article_id}`}
                    className="
                      group relative grid grid-cols-1 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-5 sm:gap-6
                      rounded-2xl border border-border-subtle bg-surface-paper
                      p-3 sm:p-4
                      transition-[border-color,transform,box-shadow] duration-300 ease-out-soft
                      hover:border-amber/45 hover:-translate-y-0.5 hover:shadow-card-hover
                    "
                  >
                    <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent opacity-0 transition-opacity duration-400 group-hover:opacity-100" />

                    {/* Image stack */}
                    <div className="min-w-0">
                      {hero && (
                        <div className="relative overflow-hidden rounded-xl bg-surface-overlay aspect-[16/10] ring-1 ring-inset ring-white/5">
                          <img
                            src={`/articles/${hero}`}
                            alt={i.article_title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out-soft group-hover:scale-[1.06]"
                          />
                          {/* Bottom gradient for legibility of badge */}
                          <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
                          {/* Photo count badge - top right */}
                          {totalImages > 1 && (
                            <span className="
                              absolute top-2.5 right-2.5
                              inline-flex items-center gap-1 rounded-full
                              border border-white/15 bg-black/55 backdrop-blur-md
                              px-2.5 py-1
                              text-[10px] font-bold uppercase tracking-[0.12em] text-white
                            ">
                              <I.Camera className="h-3 w-3 text-amber" />
                              {totalImages}
                            </span>
                          )}
                          {/* Subtle amber sweep on hover */}
                          <span
                            aria-hidden
                            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-out-soft group-hover:opacity-100"
                            style={{
                              background:
                                'radial-gradient(ellipse 70% 45% at 50% 100%, rgba(251,191,36,0.18), transparent 70%)',
                            }}
                          />
                        </div>
                      )}
                      {restThumbs.length > 0 && (
                        <div className="mt-2 hidden sm:grid grid-cols-3 gap-2">
                          {restThumbs.map((img, idx) => {
                            const isLast = idx === 2 && extra > 0;
                            return (
                              <div
                                key={idx}
                                className="
                                  relative aspect-[4/3] overflow-hidden rounded-lg
                                  bg-surface-overlay
                                  ring-1 ring-inset ring-white/5
                                  transition-[transform,box-shadow] duration-300 ease-out-soft
                                  group-hover:ring-amber/30
                                "
                              >
                                <img
                                  src={`/articles/${img}`}
                                  alt=""
                                  loading="lazy"
                                  className={`
                                    h-full w-full object-cover
                                    transition-transform duration-700 ease-out-soft
                                    ${isLast ? 'opacity-50' : 'group-hover:scale-[1.06]'}
                                  `}
                                />
                                {isLast && (
                                  <span
                                    className="
                                      absolute inset-0 flex items-center justify-center
                                      text-amber font-extrabold text-[18px] tracking-[-0.02em]
                                    "
                                    style={{ background: 'rgba(10,10,11,0.55)', backdropFilter: 'blur(2px)' }}
                                  >
                                    +{extra}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Editorial column */}
                    <div className="min-w-0 flex flex-col">
                      <div className="inline-flex items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-0.5 self-start">
                        <I.Calendar className="h-3 w-3 text-amber" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">{i.article_published_date}</span>
                      </div>
                      <h2 className="mt-3 text-[19px] sm:text-[22px] md:text-[24px] font-bold tracking-[-0.022em] text-fg-primary leading-[1.2] transition-colors duration-200 group-hover:text-amber line-clamp-3">
                        {i.article_title}
                      </h2>
                      <div aria-hidden className="mt-2.5 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0 transition-[width] duration-400 ease-out-soft group-hover:w-16" />
                      <div className="mt-3 text-[13px] leading-[1.7] text-fg-secondary line-clamp-3 [&_*]:text-fg-secondary">
                        {parse(String(i.article_desc || '').slice(0, 400) + '…')}
                      </div>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-amber transition-[gap] duration-200 group-hover:gap-2.5">
                        Read more
                        <I.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </RouterLink>
                );
              })}
            </div>
          )}
        </Section>
      </PageContainer>
    </>
  );
};
